import { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { useMutation } from '@zenbusiness/application-commons-graphql';

import { CURRENCY } from '#client/constants/currencies';
import { BANKING_PAYMENT_TYPE } from '#client/constants/payment';
import { MAP_UNIT_STATUS_TO_ZENBUSINESS_RESPONSE } from '#client/constants/transfer';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import { useLogError } from '#client/hooks/useLogError';
import usePlaid from '#client/hooks/usePlaid';
import useToastContext from '#client/hooks/useToastContext';
import useUnitAccountId from '#client/hooks/useUnitAccountId';
import { DEPOSIT_MUTATION } from '#client/mutations';
import { retrievedGraphQLError } from '#client/utils/errors';

import useCheckTransactionContext from './useCheckTransactionContext';

export const DepositContext = createContext();
DepositContext.displayName = 'DepositContext';

const ACCESS_NOT_GRANTED = 'ACCESS_NOT_GRANTED';
const ITEM_LOGIN_REQUIRED = 'ITEM_LOGIN_REQUIRED';

const needRelinkList = [ACCESS_NOT_GRANTED, ITEM_LOGIN_REQUIRED];

const useDepositContext = () => {
  const context = useContext(DepositContext);

  if (typeof context === 'undefined') {
    throw new Error('useDepositContext must be used within a DepositContext.Provider');
  }

  return context;
};

export function DepositProvider({ children }) {
  const [saveCounterpartyId, setSaveCounterpartyId] = useState(null);
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { unitAccountId } = useUnitAccountId();
  const { createErrorToast, clearAllToast } = useToastContext();
  const { refetchForFirstTransaction, refetchForFirstPayment } = useCheckTransactionContext();

  const [depositMutation, { loading, error, errors }] = useMutation(DEPOSIT_MUTATION, {
    noThrow: true
  });
  useLogError(errors, error);

  const [depositData, setDepositData] = useState(null);
  const [depositError, setDepositError] = useState(null);

  const { updatePlaidLinkToken } = usePlaid({
    unitAccountId,
    businessEntityUuid,
    isRelink: true,
    counterPartyId: saveCounterpartyId
  });

  const handleRelink = useCallback(async () => {
    clearAllToast();
    const { error: relinkError } = await updatePlaidLinkToken();
    // handle show error
    if (relinkError) {
      createErrorToast({
        title: retrievedGraphQLError(relinkError),
        error: relinkError
      });
    }
  }, [clearAllToast, createErrorToast, updatePlaidLinkToken]);

  const handleUnitReason = useCallback(
    (reason) => {
      const unitErrorMessage = MAP_UNIT_STATUS_TO_ZENBUSINESS_RESPONSE[reason];

      if (unitErrorMessage) {
        clearAllToast();
        createErrorToast({
          title: 'Deposit failed',
          description: unitErrorMessage
        });
      }
    },
    [clearAllToast, createErrorToast]
  );

  const handleError = useCallback(
    (responseError) => {
      const message = responseError?.message;
      const statusCode = responseError?.statusCode;

      clearAllToast();

      /**
       * If the error is related to the plaid link token, we show a toast
       * with a button to relink the account
       */
      if (statusCode === 400 && needRelinkList.includes(message)) {
        createErrorToast({
          title: 'Deposit failed',
          description: 'This account is no longer linked.',
          secondaryAction: {
            label: 'Relink',
            size: 'sm',
            onClick: handleRelink
          },
          error: responseError
        });
        return;
      }

      /**
       * Other errors, we just show a generic error message
       */
      createErrorToast({
        title: 'Deposit failed',
        description: 'There was an issue processing your request. Please try again.',
        error: responseError
      });
    },
    [clearAllToast, createErrorToast, handleRelink]
  );

  const handleResponse = useCallback(
    (response) => {
      const { error: responseError, data } = response;

      const reason = data?.createBankingPayment?.reason;
      if (reason) {
        handleUnitReason(reason);
        return;
      }

      if (responseError) {
        handleError(responseError);
        return;
      }

      refetchForFirstTransaction();
      refetchForFirstPayment();
    },
    [handleError, handleUnitReason, refetchForFirstPayment, refetchForFirstTransaction]
  );

  const deposit = useCallback(
    async ({ counterpartyId, idempotencyKey, customerToken, amount, description }) => {
      setSaveCounterpartyId(counterpartyId);
      const response = await depositMutation({
        variables: {
          input: {
            businessEntityUuid,
            amount: {
              currency: CURRENCY.USD,
              amount
            },
            counterpartyId,
            description,
            idempotencyKey,
            customerToken,
            type: BANKING_PAYMENT_TYPE.DEPOSIT
          }
        }
      });

      handleResponse(response);
    },
    [businessEntityUuid, handleResponse, depositMutation]
  );

  const depositV2 = useCallback(
    async ({ counterpartyId, idempotencyKey, customerToken, amount, description }) => {
      setSaveCounterpartyId(counterpartyId);
      const response = await depositMutation({
        variables: {
          input: {
            businessEntityUuid,
            amount: {
              currency: CURRENCY.USD,
              amount
            },
            counterpartyId,
            description,
            idempotencyKey,
            customerToken,
            type: BANKING_PAYMENT_TYPE.DEPOSIT
          }
        }
      });

      setDepositData(response.data?.createBankingPayment);
      setDepositError(response.error);
    },
    [businessEntityUuid, depositMutation]
  );

  // Deposit data must be cleared after deposit successful or failed
  const clearDepositData = useCallback(() => {
    setDepositData(null);
    setDepositError(null);
  }, []);

  const value = useMemo(
    () => ({
      deposit,
      depositV2,
      clearDepositData,
      loading,
      depositData,
      depositError
    }),
    [deposit, depositV2, clearDepositData, loading, depositData, depositError]
  );

  return <DepositContext.Provider value={value}>{children}</DepositContext.Provider>;
}

export default useDepositContext;
